import { ReservationContext } from "Contexts/ReservationContext";
import { RestaurantContext, Severity } from "Contexts/RestaurantContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SpecialDay } from "types/calendar";
import { ReservationStatus } from "types/reservations";
import { Date as DateType } from "types/shifts";
import { db } from "utils/firebase";
import { dateHelper, findOverlaps, reservationToSnippet } from "utils/helper";
import { RichShift } from "utils/types";
import { applyDayOverrideChanges, dayOverrideChanges, effectiveShiftSettings } from 'utils/dayOverrides';
import { ReservationShift } from 'types/shifts';
import { triggerCustomEvent } from './useCustomEvent';
import { SecurityGrant, trackChange } from 'utils/securityActivity/grant';

const useStatistics = (dateToModify?: string, update?: () => void) => {
  const { restaurantId, newToast } = useContext(RestaurantContext);
  const {
    reservations,
    date: contextDate,
    shifts,
    occassions = [],
  } = useContext(ReservationContext);

  const [openModal, setOpenModal] = useState<string | null>(null);

  const date = openModal ?? dateToModify ?? contextDate ?? dateHelper();

  const [dateData, setDateData] = useState<DateType | null>(null);
  const loadedDate = useRef<DateType | null>(null);
  const savingDateData = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadedDate.current = null;
    setDateData(null);
    const getDateData = async () => {
      if (!restaurantId || !date) return;
      const datesRef = await db
        .collection(`restaurants/${restaurantId}/reservationDates`)
        .doc(date)
        .get();
      if (!cancelled && datesRef.exists) {
        loadedDate.current = datesRef.data() as DateType;
        setDateData(loadedDate.current);
      }
    };
    getDateData();
    return () => { cancelled = true; };
  }, [date, openModal, restaurantId]);

  const [specialDay, setspecialDay] = useState<null | SpecialDay>(null);

  const [averagePerWDay, setAveragePerWDay] = useState<null | {
    [key: number]: number[];
  }>(null);

  const reservationSnippets = useMemo(
    () =>
      reservations.filter((r) => !r.blockedFullShift).map(reservationToSnippet),
    [reservations]
  );

  const { richShifts, otherReservations } = useMemo(() => {
    let richShifts: RichShift[] = [];
    let resaIds: string[] = [];

    (shifts ?? []).forEach((s) => {
      let filteredRes = reservations.filter(
        (r) =>
          !r.blockedFullShift &&
          (s.occassions?.includes(r.occassion) ||
            s.spaces?.includes(r?.space ?? "")) &&
          findOverlaps(r, s.start || 0, s.close || 0, true) &&
          r.status !== ReservationStatus.FAILED
      );

      richShifts.push({
        ...s,
        reservations: filteredRes.map(reservationToSnippet),
      });

      resaIds.push(...filteredRes.map((r) => r.id));
    });

    let otherReservations = reservations
      .filter(
        (r) => !resaIds.includes(r.id) && r.status !== ReservationStatus.FAILED
      )
      .map(reservationToSnippet);

    return { richShifts, otherReservations };
  }, [reservations, shifts]);

  const reservationsByOccassions = useMemo(() => {
    return occassions.map((occ) => ({
      value: reservationSnippets
        .filter(
          (res) =>
            res.occassion === occ.id && res.status !== ReservationStatus.FAILED
        )
        .reduce((acc: number, cV) => acc + cV.guests, 0),
      label: occ.title,
      id: occ.id,
    }));
  }, [reservationSnippets, occassions]);

  const reservationBySource = useMemo(() => {
    let sourceTable = reservationSnippets
      .filter((res) => res.status !== ReservationStatus.FAILED)
      .reduce((acc: any, cV) => {
        if (!acc[cV.source]) {
          acc[cV.source] = cV.guests;
        } else {
          acc[cV.source] += cV.guests;
        }

        return acc;
      }, {});

    return Object.keys(sourceTable).map((id) => ({
      id,
      label: id,
      value: sourceTable[id],
    }));
  }, [reservationSnippets]);

  useEffect(() => {
    if (!restaurantId) return;
    const getAverage = async () => {
      const statRef = await db
        .collection(`restaurants/${restaurantId}/statistics`)
        .doc("byWeekday")
        .get();

      if (statRef.exists) {
        setAveragePerWDay(
          statRef.data() as {
            [key: number]: number[];
          }
        );
      }
    };
    getAverage();
  }, [restaurantId]);

  const dateAvg = useMemo(() => {
    const dateDay = new Date(date).getDay();
    const dayArr = averagePerWDay ? averagePerWDay[dateDay] : null;
    return dayArr
      ? Math.floor(
          dayArr.reduce((acc: number, num: number) => (acc += num), 0) /
            dayArr.length
        )
      : null;
  }, [date, averagePerWDay]);

  const shiftOptions = useMemo(() => {
    return [
      { id: null, label: "All day" },
      ...(dateData?.shifts
        ?.slice().sort((a, b) => (a?.start || 0) - (b?.start || 0))
        .map((s) => ({ id: s.id, label: s.name || "" })) || []),
    ];
  }, [dateData?.shifts]);

  useEffect(() => {
    if (!restaurantId || !date) return;
    const getCalendar = async () => {
      if (restaurantId) {
        const calendarRef = await db
          .collection(`restaurants/${restaurantId}/calendarSheets`)
          .doc(date.slice(0, 7))
          .get();

        if (calendarRef.exists) {
          const data = calendarRef.data();
          const specialDayOnDate = data?.specialDays.find(
            (sD: SpecialDay) => sD.date === date
          );
          setspecialDay(specialDayOnDate || null);
        }
      }
    };
    getCalendar();
  }, [date, restaurantId]);

  const isOpen = useMemo(() => {
    if (!shifts?.length) return false;

    let closedOccassions: Record<string, 0 | 1> =
      shifts?.reduce((acc, cV) => {
        return {
          ...acc,
          ...cV.occassions?.reduce(
            (acc1, id) => ({
              ...acc1,
              [id]: cV.closed ? 0 : acc[id] === 0 || 1,
            }),
            {}
          ),
        };
      }, occassions.reduce((acc, cV) => ({ ...acc, [cV.id]: 1 }), {}) as Record<string, 0 | 1>) ??
      {};

    // console.log({ closedOccassions, shifts });

    return !!Object.keys(closedOccassions).filter(
      (x) => closedOccassions?.[x] === 1
    ).length;
  }, [shifts, occassions]);

  // console.log({ specialDay, isOpen });

  /** `grant`: the Security Plus PIN that opened the dialog; the save is logged under it. */
  const submitDateData = async (grant?: SecurityGrant) => {
    if (!dateData?.date || savingDateData.current) return;
    savingDateData.current = true;
    try {
      const ref = db.collection(`restaurants/${restaurantId}/reservationDates`).doc(dateData.date);
      const changes = dateData.shifts.map(shift => ({
        id: shift.id,
        changes: dayOverrideChanges(
          (loadedDate.current?.shifts.find(before => before.id === shift.id) ?? shift) as ReservationShift,
          shift as ReservationShift
        ),
      })).filter(({ changes }) => Object.keys(changes.set).length || changes.reset.length);
      const save = db.runTransaction(async transaction => {
        const snapshot = await transaction.get(ref);
        const current = snapshot.data() as DateType | undefined;
        if (!current?.shifts) throw new Error('Der Tag konnte nicht geladen werden. Bitte erneut öffnen.');
        const shifts = current.shifts.map(shift => {
          const change = changes.find(change => change.id === shift.id);
          return change ? applyDayOverrideChanges(shift as ReservationShift, change.changes) : shift;
        });
        if (changes.some(change => !current.shifts.some(shift => shift.id === change.id))) {
          throw new Error('Eine Schicht wurde inzwischen entfernt. Bitte erneut öffnen.');
        }
        const events = Array.from(new Set([
          ...(current.events ?? []),
          ...current.shifts.flatMap(shift => {
            const id = effectiveShiftSettings(shift as ReservationShift).eventV02?.id;
            return id ? [id] : [];
          }),
          ...shifts.flatMap(shift => {
            const id = effectiveShiftSettings(shift as ReservationShift).eventV02?.id;
            return id ? [id] : [];
          }),
        ]));
        if (changes.length) transaction.update(ref, { shifts, events });
      });
      // Nothing changed is nothing to log.
      await (changes.length
        ? trackChange(grant, 'day.reservationsSave', save, {
            targets: [
              { type: 'day', id: dateData.date },
              ...changes.map(({ id }) => ({ type: 'shift' as const, id })),
            ],
          })
        : save);
      triggerCustomEvent('triggerShiftReload');
      if (update) update();
      setDateData(null);
      setOpenModal(null);
    } catch (error: any) {
      newToast(error.message ?? 'Die Tagesausnahmen konnten nicht gespeichert werden.', Severity.ERROR);
    } finally {
      savingDateData.current = false;
    }
  };

  return {
    date,
    dateAvg,
    shiftOptions,
    isOpen,
    richShifts,
    reservationsByOccassions,
    reservationBySource,
    reservationSnippets,
    dateData,
    setDateData,
    submitDateData,
    openModal,
    setOpenModal,
    otherReservations,
  };
};

export default useStatistics;
